// 掃碼上架／下架 — the counter's put-away screen.
//
// WHY THIS EXISTS. Getting a delivery onto the rail used to be five actions
// across two tabs: 進貨登記 → 送往專櫃 → 確認收貨 → 設櫃位 → 上架. The staffer
// performs ONE physical act (carry the bag over, hang the pieces up), so the
// four extra steps were pure bookkeeping — and the ledger shows they were not
// performed: 運送中 was used once ever, and of 168 in-store variants only 39
// carried a 櫃位 and 69 sat 未上架. This screen collapses the whole thing into
// the act itself: pick the shelf you are filling, then scan each hang tag as
// you hang it. 櫃位 and 上架 become a byproduct of the scan, which is the only
// way they stay true.
//
// Two modes, one scanner:
//   上架 — 辦公室 −1 / 在櫃 +1 (or just the 上架 flag when the piece is already
//          at the counter, i.e. 補好了), plus the picked 櫃位.
//   下架 — 在櫃 −1 / 辦公室 +1. The piece physically leaves the counter.
// The mode is stated in the dialog's colour and in every log line, because a
// scan in the wrong mode moves stock the wrong way and looks identical.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, Stack, TextField, ToggleButton,
  ToggleButtonGroup, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import UndoIcon from '@mui/icons-material/Undo';
import AddIcon from '@mui/icons-material/Add';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import { NOKE_SLOTS } from '../../utils/nokeSlots';
import { useBarcodeScanner } from '../../hooks/useBarcodeScanner';

const SCANNER_ELEMENT_ID = 'noke-shelve-scanner';
const CAMERA_PREF_KEY = 'noke_shelve_camera';
// A camera decodes ~10 frames a second, so without this one hang tag held in
// front of the lens would shelve ten pieces. Two pieces of the SAME variant are
// added with the 再 +1 button on the newest line rather than by waiting.
const SAME_CODE_MS = 1500;

export type ScanMode = 'shelve' | 'pull';

/** Mirrors the /api/noke serializer. Declares every field the page's
 *  InventoryRow requires, so `onApplied` accepts the page's row merger. */
export interface ScanInventoryItem {
  variant_id: number;
  sku: string;
  product_title: string;
  vendor: string;
  color: string;
  size: string;
  price: number;
  qty_on_hand: number;
  barcode: string;
  updated_at: string;
  qty_office?: number;
  qty_in_transit?: number;
  storage_slot?: string | null;
  on_display?: boolean;
  custom_barcode?: string | null;
  image_url?: string | null;
}

interface ScanResponse {
  item: ScanInventoryItem;
  action: ScanMode;
  /** 1 when a piece changed location; 0 when 上架 only flipped the flag. */
  moved: number;
  already_in_store: boolean;
  storage_slot: string | null;
}

interface LogEntry extends ScanResponse {
  key: string;
  /** Set once the line has been undone, so it cannot be undone twice. */
  undone?: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Merge one updated row into the table behind the dialog. */
  onApplied: (item: ScanInventoryItem) => void;
  /** Called on close when at least one scan landed, so the tab can refetch. */
  onDirtyClose: () => void;
}

const newRef = () => `scan-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

// Short beep + haptic buzz, same feedback as the POS scanner: the staffer is
// looking at the garment, not the screen.
const feedbackBeep = (ok: boolean): void => {
  try {
    const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (AudioCtx) {
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = ok ? 880 : 220;
      gain.gain.value = 0.15;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + (ok ? 0.12 : 0.3));
      osc.onended = () => ctx.close();
    }
  } catch {
    /* audio unavailable — non-fatal */
  }
  if (navigator.vibrate) {
    try { navigator.vibrate(ok ? 80 : [60, 60, 60]); } catch { /* non-fatal */ }
  }
};

const describe = (it: ScanInventoryItem) =>
  `${it.sku} ${it.product_title}`.trim() + (it.color || it.size ? ` · ${[it.color, it.size].filter(Boolean).join(' / ')}` : '');

export default function ScanShelveDialog({ open, onClose, onApplied, onDirtyClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [mode, setMode] = useState<ScanMode>('shelve');
  const [slot, setSlot] = useState<string>('');
  const [manual, setManual] = useState('');
  const [log, setLog] = useState<LogEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);

  const scannerRef = useRef<Html5Qrcode | null>(null);
  const lastScan = useRef<{ code: string; at: number }>({ code: '', at: 0 });
  // Mode and slot are read inside the camera callback, which html5-qrcode holds
  // from the moment the camera starts — refs keep it reading the CURRENT values
  // instead of the ones captured when the camera was switched on.
  const modeRef = useRef(mode);
  const slotRef = useRef(slot);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { slotRef.current = slot; }, [slot]);

  const submitScan = useCallback(async (code: string, qty = 1) => {
    const trimmed = String(code || '').trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    const action = modeRef.current;
    try {
      for (let i = 0; i < qty; i += 1) {
        const res = await api.post('/api/noke/inventory/scan', {
          barcode: trimmed,
          action,
          storage_slot: action === 'shelve' ? (slotRef.current || null) : undefined,
          client_ref: newRef(),
        });
        const data: ScanResponse = res.data;
        onApplied(data.item);
        setLog((prev) => [{ ...data, key: newRef() }, ...prev].slice(0, 100));
      }
      feedbackBeep(true);
    } catch (err: any) {
      const msg = err.response?.data?.error?.message || err.response?.data?.message || '掃碼失敗';
      setError(msg);
      feedbackBeep(false);
    } finally {
      setBusy(false);
    }
  }, [onApplied]);

  // Shared entry point for camera reads, the HID scanner and manual entry, so
  // all three get the same duplicate guard.
  const handleCode = useCallback((code: string) => {
    const trimmed = String(code || '').trim();
    if (!trimmed) return;
    const now = Date.now();
    if (lastScan.current.code === trimmed && now - lastScan.current.at < SAME_CODE_MS) return;
    lastScan.current = { code: trimmed, at: now };
    submitScan(trimmed);
  }, [submitScan]);

  // USB / Bluetooth scanner gun. Only while the dialog is open, and the hook
  // itself stays hands-off whenever focus is in a text field.
  useBarcodeScanner({ onScan: handleCode, enabled: open });

  const startCamera = useCallback(async () => {
    if (scannerRef.current) return;
    try {
      const html5 = new Html5Qrcode(SCANNER_ELEMENT_ID, {
        // NK hang tags are Code 128; MdoC pieces carry the manufacturer EAN-13,
        // which the backend resolves through custom_barcode.
        formatsToSupport: [
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.EAN_13,
        ],
        verbose: false,
      });
      scannerRef.current = html5;
      await html5.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 260, height: 120 } },
        (decoded: string) => handleCode(decoded),
        () => { /* per-frame decode miss — fires constantly, ignore */ }
      );
      setScanning(true);
      localStorage.setItem(CAMERA_PREF_KEY, '1');
    } catch {
      scannerRef.current = null;
      setScanning(false);
      localStorage.setItem(CAMERA_PREF_KEY, '0');
      enqueueSnackbar('無法開啟相機，請改用掃描槍或手動輸入條碼', { variant: 'error' });
    }
  }, [handleCode, enqueueSnackbar]);

  const stopCamera = useCallback(async (remember = true) => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    setScanning(false);
    if (remember) localStorage.setItem(CAMERA_PREF_KEY, '0');
    if (!scanner) return;
    try {
      await scanner.stop();
      await scanner.clear();
    } catch { /* already stopped — non-fatal */ }
  }, []);

  // Camera follows the dialog: opened with the preference remembered from last
  // time, and always released on close so the device is free for the POS page.
  useEffect(() => {
    if (open) {
      if (localStorage.getItem(CAMERA_PREF_KEY) === '1') startCamera();
    } else {
      stopCamera(false);
      setLog([]);
      setError(null);
      setManual('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => () => { stopCamera(false); }, [stopCamera]);

  // Undo one line. A 上架 that MOVED a piece is reversed by the opposite scan;
  // a 上架 that only set the flag (piece was already 在櫃) must not push a piece
  // into the office — it just clears 上架 again.
  const undo = async (entry: LogEntry) => {
    setBusy(true);
    setError(null);
    try {
      let item: ScanInventoryItem;
      if (entry.moved === 0) {
        const res = await api.post('/api/noke/inventory/set-slot', {
          variant_id: entry.item.variant_id, on_display: false,
        });
        item = res.data.item;
      } else {
        const res = await api.post('/api/noke/inventory/scan', {
          barcode: entry.item.barcode,
          action: entry.action === 'shelve' ? 'pull' : 'shelve',
          client_ref: newRef(),
        });
        item = res.data.item;
      }
      onApplied(item);
      setLog((prev) => prev.map((l) => (l.key === entry.key ? { ...l, undone: true, item } : l)));
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.response?.data?.message || '復原失敗');
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (log.some((l) => !l.undone)) onDirtyClose();
    onClose();
  };

  const shelved = log.filter((l) => !l.undone && l.action === 'shelve').length;
  const pulled = log.filter((l) => !l.undone && l.action === 'pull').length;
  const isShelve = mode === 'shelve';
  const accent = isShelve ? theme.palette.success.main : theme.palette.warning.main;

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderTop: `6px solid ${accent}` }}>
        <Box sx={{ flexGrow: 1 }}>{isShelve ? '掃碼上架' : '掃碼下架'}</Box>
        <IconButton onClick={close} size="small" aria-label="關閉"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Mode picker. Full width and colour-coded: the single most costly
            mistake on this screen is scanning a whole delivery in the wrong
            direction, and the two actions look identical once you are scanning. */}
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={mode}
          onChange={(_e, v: ScanMode | null) => { if (v) { setMode(v); setError(null); } }}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="shelve" color="success" sx={{ py: 1.25, fontWeight: 700 }}>
            上架（辦公室 → 在櫃）
          </ToggleButton>
          <ToggleButton value="pull" color="warning" sx={{ py: 1.25, fontWeight: 700 }}>
            下架（在櫃 → 辦公室）
          </ToggleButton>
        </ToggleButtonGroup>

        {isShelve ? (
          <Autocomplete
            freeSolo
            options={NOKE_SLOTS}
            value={slot}
            onChange={(_e, v) => setSlot(v || '')}
            onInputChange={(_e, v) => setSlot(v)}
            renderInput={(params) => (
              <TextField {...params} size="small" label="要放的櫃位（選填）"
                helperText="選一次，接下來掃到的每一件都會記在這個櫃位。留空則保留各商品原本的櫃位。" />
            )}
            sx={{ mb: 2 }}
          />
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            每掃一件，在櫃數量 −1、辦公室 +1，代表這件實際被拿離專櫃。只是要從櫃面收起來、東西仍留在店裡，請改用列表上的「已上架」標記。
          </Alert>
        )}

        {/* The camera's <video> must stay mounted while running, so the element
            is always rendered and only its height collapses when off. */}
        <Box
          id={SCANNER_ELEMENT_ID}
          sx={{
            width: '100%', mb: scanning ? 2 : 0, borderRadius: 1, overflow: 'hidden',
            '& video': { width: '100%' }, display: scanning ? 'block' : 'none',
          }}
        />

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            variant={scanning ? 'contained' : 'outlined'}
            startIcon={<PhotoCameraIcon />}
            onClick={() => (scanning ? stopCamera() : startCamera())}
          >
            {scanning ? '關閉相機' : '用相機掃'}
          </Button>
          <TextField
            size="small"
            fullWidth
            label="或手動輸入條碼 / SKU"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              const code = manual.trim();
              if (!code) return;
              setManual('');
              // Manual entry bypasses the duplicate guard on purpose: typing the
              // same code twice is a deliberate second piece, not a double read.
              submitScan(code);
            }}
          />
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          掃描槍可直接掃，不用先點任何欄位。同一個條碼 1.5 秒內只算一次；同款要再上架一件，請按下方最新一筆的「再 +1」。
        </Typography>

        {busy && <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={22} /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Divider sx={{ mb: 1 }}>
          <Chip size="small" label={`本次 上架 ${shelved} 件 · 下架 ${pulled} 件`} />
        </Divider>

        {log.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
            {isShelve ? '掃第一件吊牌開始上架' : '掃第一件吊牌開始下架'}
          </Box>
        ) : (
          <Stack divider={<Divider />}>
            {log.map((entry, idx) => (
              <Box key={entry.key} sx={{ py: 1, opacity: entry.undone ? 0.45 : 1 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{describe(entry.item)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {entry.undone
                        ? '已復原'
                        : entry.action === 'pull'
                          ? '已下架 · 移回辦公室'
                          : entry.already_in_store
                            ? '本來就在櫃 · 已標記上架'
                            : '已從辦公室上架'}
                      {' · '}在櫃 {entry.item.qty_on_hand}
                      {(entry.item.qty_office ?? 0) > 0 && ` · 辦公室 ${entry.item.qty_office}`}
                      {entry.storage_slot && ` · 櫃位 ${entry.storage_slot}`}
                    </Typography>
                  </Box>
                  {!entry.undone && idx === 0 && (
                    <Button size="small" startIcon={<AddIcon />} disabled={busy}
                      onClick={() => submitScan(entry.item.barcode)}>
                      再 +1
                    </Button>
                  )}
                  {!entry.undone && (
                    <IconButton size="small" aria-label="復原" disabled={busy} onClick={() => undo(entry)}>
                      <UndoIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={close} variant="contained">完成</Button>
      </DialogActions>
    </Dialog>
  );
}
